import { defineStore } from 'pinia';
import {
  hasAuthSession,
  getUserId,
  getWalletAddress,
  getWalletProviderId,
} from '@/utils/auth-token';
import { getAuthGeneration } from '@/utils/auth-teardown';
import { ensureUserSessionProfile, getSessionLoginType } from '@/utils/user-session-profile';
import {
  checkCustodialMoneyOpsGate,
  sessionExpectsCustodialWallet,
} from '@/utils/custodial-session-gate';
import {
  awaitPendingSilentCustodialBind,
  completePrivyAfterPlatformLogin,
  isPrivyDepositRequiredError,
  isSilentCustodialBindPending,
  syncCustodialSessionFromTradeAddresses,
} from '@/utils/privy-platform-login';

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasLocalCustodialWalletState() {
  return getWalletProviderId() === 'privy' && Boolean(String(getWalletAddress() || '').trim());
}

/** @type {Promise<{ ok: boolean, code?: string }>|null} */
let inflightEnsure = null;
let inflightGeneration = -1;
let inflightUserId = '';
let ensureRunId = 0;

function currentAuthSession() {
  return {
    generation: getAuthGeneration(),
    userId: String(getUserId() ?? '').trim(),
  };
}

function isEnsureRunCurrent(run) {
  const current = currentAuthSession();
  return (
    run.runId === ensureRunId &&
    run.generation === current.generation &&
    run.userId === current.userId &&
    hasAuthSession()
  );
}

/**
 * 托管通道就绪态：登录后 / 已登录刷新静默 ensure + 失败重试；
 * 充提弹窗订阅此状态展示 loading/重试。
 */
export const useCustodialSessionStore = defineStore('custodialSession', {
  state: () => ({
    /** @type {'idle'|'loading'|'ready'|'error'|'deferred'} */
    status: 'idle',
    /** @type {''|'BINDING'|'LIST_FAILED'|'NOT_BOUND'|'DEPOSIT_REQUIRED'|'UNKNOWN'} */
    errorCode: '',
    attempt: 0,
  }),

  getters: {
    isReady: (s) => s.status === 'ready',
    isLoading: (s) => s.status === 'loading',
    isError: (s) => s.status === 'error',
    /**
     * 已登录托管用户但本地 Privy/地址未写全，或上次 ensure 失败 / 尚未跑过。
     * 用于冷启动、回前台、进个人中心时兜底重跑静默授权。
     * deferred：等待首次充值，勿在登录后反复撞 custom-token。
     */
    needsRepair() {
      if (!hasAuthSession()) return false;
      if (!sessionExpectsCustodialWallet()) return false;
      if (this.status === 'deferred') return false;
      if (this.status === 'error' || this.status === 'idle') return true;
      return !hasLocalCustodialWalletState();
    },
  },

  actions: {
    reset() {
      ensureRunId += 1;
      inflightEnsure = null;
      inflightGeneration = -1;
      inflightUserId = '';
      this.status = 'idle';
      this.errorCode = '';
      this.attempt = 0;
    },

    /**
     * 已登录场景兜底：需要修复或未就绪时自动 ensure（含失败重试）。
     * @param {{ maxAttempts?: number }} [opts]
     */
    async ensureIfNeeded(opts = {}) {
      if (!hasAuthSession()) {
        this.reset();
        return { ok: false, code: 'UNKNOWN' };
      }
      // deferred：先 list 水合（服务端预建）；无地址且非 force 时勿撞 custom-token。
      // force=true（如充值弹窗）：拉长轮询 list，避免与 Ensure 并行 SDK create 第二钱包。
      if (this.status === 'deferred') {
        try {
          const hydrateDelays = opts.force
            ? [0, 800, 1600, 3200, 5000, 8000]
            : [0];
          for (let i = 0; i < hydrateDelays.length; i += 1) {
            if (hydrateDelays[i] > 0) await delay(hydrateDelays[i]);
            const synced = await syncCustodialSessionFromTradeAddresses();
            if (synced && hasLocalCustodialWalletState()) {
              this.status = 'ready';
              this.errorCode = '';
              return { ok: true };
            }
          }
        } catch (err) {
          console.warn('[custodial-session] deferred hydrate failed', err);
        }
        if (!opts.force) {
          return { ok: false, code: 'DEPOSIT_REQUIRED' };
        }
      }
      await ensureUserSessionProfile();
      if (!hasAuthSession()) return { ok: false, code: 'UNKNOWN' };
      if (!sessionExpectsCustodialWallet()) {
        this.status = 'ready';
        this.errorCode = '';
        return { ok: true };
      }
      if (this.status === 'ready' && !this.needsRepair) {
        return { ok: true };
      }
      return this.ensure({ force: true, maxAttempts: opts.maxAttempts });
    },

    /**
     * @param {{ force?: boolean, maxAttempts?: number }} [opts]
     * @returns {Promise<{ ok: boolean, code?: string }>}
     */
    async ensure(opts = {}) {
      const force = Boolean(opts.force);
      const maxAttempts = Math.max(1, Number(opts.maxAttempts) || 3);

      if (!hasAuthSession()) {
        this.reset();
        return { ok: false, code: 'UNKNOWN' };
      }

      await ensureUserSessionProfile();
      if (!hasAuthSession()) return { ok: false, code: 'UNKNOWN' };

      const mustRepair = this.needsRepair;
      if (!force && this.status === 'deferred') {
        return { ok: false, code: 'DEPOSIT_REQUIRED' };
      }
      if (!force && !mustRepair && this.status === 'ready') {
        return { ok: true };
      }

      const session = currentAuthSession();
      if (
        inflightEnsure &&
        inflightGeneration === session.generation &&
        inflightUserId === session.userId
      ) {
        return inflightEnsure;
      }
      if (inflightEnsure) ensureRunId += 1;

      this.status = 'loading';
      this.errorCode = '';

      const run = { ...session, runId: ++ensureRunId };
      inflightGeneration = session.generation;
      inflightUserId = session.userId;
      const task = this._runEnsure(maxAttempts, run).finally(() => {
        if (inflightEnsure !== task) return;
        inflightEnsure = null;
        inflightGeneration = -1;
        inflightUserId = '';
      });
      inflightEnsure = task;
      return task;
    },

    /** 弹窗内「重试」 */
    retry() {
      return this.ensure({ force: true, maxAttempts: 3 });
    },

    /** 首次充值后静默绑定成功：标记就绪，避免仍停在 deferred */
    markReadyAfterBind() {
      this.status = 'ready';
      this.errorCode = '';
    },

    /**
     * @param {number} maxAttempts
     * @returns {Promise<{ ok: boolean, code?: string }>}
     */
    async _runEnsure(maxAttempts, run) {
      this.status = 'loading';
      this.errorCode = '';
      this.attempt = 0;

      if (!sessionExpectsCustodialWallet()) {
        this.status = 'ready';
        return { ok: true };
      }

      try {
        await awaitPendingSilentCustodialBind();
      } catch (err) {
        console.warn('[custodial-session] await silent bind failed', err);
      }
      if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };

      let lastCode = 'UNKNOWN';

      for (let i = 0; i < maxAttempts; i += 1) {
        this.attempt = i + 1;
        if (i > 0) {
          await delay(1000 * 2 ** (i - 1));
        }
        if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };

        if (isSilentCustodialBindPending()) {
          try {
            await awaitPendingSilentCustodialBind();
          } catch {
            /* continue */
          }
        }
        if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };

        try {
          await syncCustodialSessionFromTradeAddresses();
        } catch (err) {
          console.warn('[custodial-session] sync trade-address failed', err);
        }
        if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };

        let gate = await checkCustodialMoneyOpsGate();
        if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
        if (gate.ok && hasLocalCustodialWalletState()) {
          this.status = 'ready';
          this.errorCode = '';
          return { ok: true };
        }

        // 门禁已过但本地未写入 provider：再 sync；仍没有则走静默重授权
        if (gate.ok && !hasLocalCustodialWalletState()) {
          const synced = await syncCustodialSessionFromTradeAddresses();
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          if (synced && hasLocalCustodialWalletState()) {
            this.status = 'ready';
            this.errorCode = '';
            return { ok: true };
          }
          lastCode = 'NOT_BOUND';
        }

        if (gate.code === 'BINDING') {
          lastCode = 'BINDING';
          try {
            await awaitPendingSilentCustodialBind();
          } catch {
            /* continue */
          }
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          gate = await checkCustodialMoneyOpsGate();
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          if (gate.ok) {
            await syncCustodialSessionFromTradeAddresses();
            if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
            if (hasLocalCustodialWalletState() || gate.ok) {
              // sync 成功最理想；门禁 ok 时尽量已写入 privy
              if (!hasLocalCustodialWalletState()) {
                await syncCustodialSessionFromTradeAddresses();
                if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
              }
              if (hasLocalCustodialWalletState()) {
                this.status = 'ready';
                this.errorCode = '';
                return { ok: true };
              }
            }
          }
        }

        // NOT_BOUND / LIST_FAILED / 本地 Privy 未写入：重启静默 Privy 授权
        lastCode = gate.ok ? lastCode || 'NOT_BOUND' : gate.code || 'NOT_BOUND';
        try {
          await completePrivyAfterPlatformLogin({
            originLoginType: getSessionLoginType() || undefined,
          });
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          await syncCustodialSessionFromTradeAddresses();
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          gate = await checkCustodialMoneyOpsGate();
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          if (gate.ok && hasLocalCustodialWalletState()) {
            this.status = 'ready';
            this.errorCode = '';
            return { ok: true };
          }
          if (gate.ok) {
            await syncCustodialSessionFromTradeAddresses();
            if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
            if (hasLocalCustodialWalletState()) {
              this.status = 'ready';
              this.errorCode = '';
              return { ok: true };
            }
          }
          lastCode = gate.code || lastCode;
        } catch (err) {
          if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
          if (isPrivyDepositRequiredError(err)) {
            this.status = 'deferred';
            this.errorCode = 'DEPOSIT_REQUIRED';
            console.info(
              '[custodial-session] Privy deferred until first deposit',
              err?.message || err
            );
            return { ok: false, code: 'DEPOSIT_REQUIRED' };
          }
          console.warn('[custodial-session] silent Privy re-auth failed', err);
          if (await syncCustodialSessionFromTradeAddresses()) {
            if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
            const gateAfterSync = await checkCustodialMoneyOpsGate();
            if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
            if (gateAfterSync.ok && hasLocalCustodialWalletState()) {
              this.status = 'ready';
              this.errorCode = '';
              return { ok: true };
            }
            lastCode = gateAfterSync.code || lastCode;
          }
        }
      }

      if (!isEnsureRunCurrent(run)) return { ok: false, code: 'UNKNOWN' };
      this.status = 'error';
      this.errorCode = lastCode;
      return { ok: false, code: lastCode };
    },
  },
});
